import {
  EdgeNode,
  EdgeNodeArchitectureEnum,
  EdgeSchedulingTypeEnum,
  KubernetesTaintEffectEnum,
} from '@cast/types-omni';

import { ProvisionNodesCloudConfigurationFormValues } from '../types';
import { OMNI_TAINT_EFFECT_BY_TEMPLATE_TAINT_EFFECT } from './_constants';

type BuildNodeConfigOptions = {
  values: ProvisionNodesCloudConfigurationFormValues;
  nodeArchitecture: EdgeNodeArchitectureEnum;
};

export const buildNodeConfig = ({
  values,
  nodeArchitecture,
}: BuildNodeConfigOptions): EdgeNode => {
  const config: EdgeNode = {
    name: values.name ?? '',
    nodeArchitecture,
    instanceType: values.instanceType ?? '',
    zone: values.availabilityZone ?? '',
    configurationId: values.edgeConfigurationId,
    schedulingType:
      values.resourceOffering === 'spot'
        ? EdgeSchedulingTypeEnum.SPOT
        : EdgeSchedulingTypeEnum.ON_DEMAND,
    deleteProtection: values.deleteProtection,
  };

  if (values.bootDiskGib) {
    config.bootDiskGib = values.bootDiskGib;
  }

  if (values.gpuType && values.gpuCount) {
    config.gpuConfig = {
      type: values.gpuType,
      count: values.gpuCount,
    };
  }

  if (values.replicas) {
    config.gpuConfig = {
      ...config.gpuConfig,
      timeSharing: {
        replicas: values.replicas,
      },
    };
  }

  if (values.partitionSize) {
    config.gpuConfig = {
      ...config.gpuConfig,
      mig: {
        partitionSizes: [values.partitionSize],
      },
    };
  }

  // Kubernetes orchestration - taints
  if (values.shouldTaint && values._customTaints?.length) {
    const taints = values._customTaints
      .filter((taint) => taint.key)
      .map((taint) => ({
        key: taint.key,
        value: taint.value ?? '',
        effect: taint.effect
          ? OMNI_TAINT_EFFECT_BY_TEMPLATE_TAINT_EFFECT[taint.effect]
          : KubernetesTaintEffectEnum.TAINT_EFFECT_UNSPECIFIED,
      }));

    if (taints.length > 0) {
      config.kubernetesTaints = taints;
    }
  }

  // Kubernetes orchestration - labels
  if (values.shouldUseCustomLabels && values._customLabels?.length) {
    const labels = values._customLabels
      .filter((label) => label.key)
      .reduce((acc, label) => {
        acc[label.key] = label.value ?? '';
        return acc;
      }, {} as Record<string, string>);

    if (Object.keys(labels).length > 0) {
      config.kubernetesLabels = labels;
    }
  }

  return config;
};
